<template>
  <div
    data-cell="true"
    data-footer-cell="true"
    :data-row="row"
    :data-col="col"
    :class="cn(
      'font-ds text-foreground bg-accent dark:bg-background data-row-hovered:bg-accent/75 data-row-hovered:dark:bg-background/75 z-10 min-h-8 py-4.75 px-4 flex items-center justify-end cursor-cell',
      'relative border-t! border-t-primary/20!',
      label ? 'sticky justify-start text-left left-0 text-xs tracking-ds-caps uppercase flex-auto z-20 font-semibold' : 'sticky font-bold number-node',
      textColorClass,
    )"
    :data-raw-value="typeof rawValue === 'number' ? rawValue : undefined"
    :data-value-field="field"
  >
    <button
      v-if="isClickableValue && typeof rawValue === 'number'"
      type="button"
      :class="cn('truncate text-sm sm:text-xs focus-visible:outline-none focus-visible:shadow-ds-focus rounded-ds-sm', CLICKABLE_TEXT_CLASS)"
      @click="handleClick"
    >
      {{ text }}
    </button>
    <div
      v-else
      class="truncate text-sm sm:text-xs"
    >
      {{ text }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { CLICKABLE_TEXT_CLASS } from '@/components/DS/patterns/Pivot/utils/const'
import { cn } from '@/helpers/cn'

type Props = {
  text: string
  /** The row-label cell (left side, "Grand total") vs a value cell. */
  label?: boolean
  rawValue?: unknown
  field?: string
  /** Footer row index — `data.length`, one past the last body row. See `usePivotSelection`. */
  row: number
  /** 0-based leaf-column position, left to right. */
  col: number
  /** This field is in `SIGN_COLORED_FIELDS_PROVIDE_KEY` — color a nonzero `rawValue` red/green by sign. */
  signColored?: boolean
  /** Whether the cell is clickable. */
  isClickableValue?: boolean
}

type Emits = {
  click: [value: number]
}

const { rawValue, signColored } = defineProps<Props>()
const emit = defineEmits<Emits>()

const handleClick = () => {
  if (typeof rawValue === 'number') emit('click', rawValue)
}

// `-subtle-foreground`, not the bare `success`/`destructive` fill — see `PivotValueCell`'s own
// comment on why. Zero stays neutral.
const textColorClass = computed(() => {
  if (!signColored || typeof rawValue !== 'number' || rawValue === 0) return undefined
  return rawValue < 0 ? 'text-destructive-subtle-foreground' : 'text-success-subtle-foreground'
})
</script>
